import React, { useEffect } from 'react';
import { Link } from '../routing';
import { Sparkles } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import TraderIdentity from '../components/TraderIdentity';
import { useCompany } from '../utils/useCompany';
import PageHero from '../components/PageHero';
import theme from '../theme';

const LINK = { color: theme.gold };

// Inline markup used inside translated sentences. Keeping the email address out
// of the strings means it is never duplicated or translated — and building the
// set from configuration means the <mail/> link in a clause like "contact us at
// …" is the same address the invoices carry. It used to be a literal pointing at
// glamgo.cy, a domain with no MX record: the clause told the customer to write
// to a mailbox that could not receive.
function makeComponents(email) {
  return {
    b: <strong />,
    i: <em />,
    br: <br />,
    mail: <a href={`mailto:${email}`} style={LINK}>{email}</a>,
    wform: <Link to="/withdrawal-form" style={LINK} />,
  };
}

export default function Terms() {
  const { t } = useTranslation();
  const company = useCompany();
  const COMPONENTS = makeComponents(company.email);
  const T = ({ k }) => <Trans i18nKey={k} components={COMPONENTS} />;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ fontFamily: theme.font.body, background: theme.cream, minHeight: '100vh' }}>
      {/* Navbar */}
      <nav style={{ background: theme.espresso, padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <Sparkles size={18} color={theme.gold} />
          <span style={{ fontFamily: theme.font.display, fontSize: 24, fontWeight: 600, color: theme.white, letterSpacing: '0.05em' }}>
            GLAM<span style={{ color: theme.gold }}>GO</span>
          </span>
        </Link>
        <Link to="/" style={{ color: theme.alpha.white(0.6), textDecoration: 'none', fontSize: 12, letterSpacing: '0.12em' }}>{t('terms.back')}</Link>
      </nav>

      {/* Header */}
      <PageHero
        eyebrow={t('terms.label')}
        title={t('terms.title')}
        subtitle={t('terms.lastUpdated', { date: t('terms.updatedDate'), version: company.termsVersion || '—' })}
        height={280}
      />

      {/* Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '64px 24px' }}>
        <Section title={t('terms.s1.title')}>
          <p><T k="terms.s1.p1" /></p>
          <p><T k="terms.s1.p2" /></p>
        </Section>

        <Section title={t('terms.s2.title')}>
          <ul>
            <li><T k="terms.s2.i1" /></li>
            <li><T k="terms.s2.i2" /></li>
            <li><T k="terms.s2.i3" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s3.title')}>
          <ul>
            <li><T k="terms.s3.i1" /></li>
            <li><T k="terms.s3.i2" /></li>
            <li><T k="terms.s3.i3" /></li>
            <li><T k="terms.s3.i4" /></li>
            <li><T k="terms.s3.i5" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s4.title')}>
          <ul>
            <li><T k="terms.s4.i1" /></li>
            <li><T k="terms.s4.i2" /></li>
            <li><T k="terms.s4.i3" /></li>
            <li><T k="terms.s4.i4" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s5.title')}>
          <ul>
            <li><T k="terms.s5.i1" /></li>
            <li><T k="terms.s5.i2" /></li>
            <li><T k="terms.s5.i3" /></li>
            <li><T k="terms.s5.i4" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s6.title')}>
          <ul>
            <li><T k="terms.s6.i1" /></li>
            <li><T k="terms.s6.i2" /></li>
            <li><T k="terms.s6.i3" /></li>
            <li><T k="terms.s6.i4" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s7.title')}>
          <p><T k="terms.s7.intro" /></p>
          <ul>
            <li><T k="terms.s7.i1" /></li>
            <li><T k="terms.s7.i2" /></li>
            <li><T k="terms.s7.i3" /></li>
            <li><T k="terms.s7.i4" /></li>
            <li><T k="terms.s7.i5" /></li>
            <li><T k="terms.s7.i6" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s8.title')}>
          <ul>
            <li><T k="terms.s8.i1" /></li>
            <li><T k="terms.s8.i2" /></li>
            <li><T k="terms.s8.i3" /></li>
            <li><T k="terms.s8.i4" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s9.title')}>
          <ul>
            <li><T k="terms.s9.i1" /></li>
            <li><T k="terms.s9.i2" /></li>
            <li><T k="terms.s9.i3" /></li>
            <li><T k="terms.s9.i4" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s10.title')}>
          <ul>
            <li><T k="terms.s10.i1" /></li>
            <li><T k="terms.s10.i2" /></li>
            <li><T k="terms.s10.i3" /></li>
            <li><T k="terms.s10.i4" /></li>
            <li><T k="terms.s10.i5" /></li>
            <li><T k="terms.s10.i6" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s11.title')}>
          <p><T k="terms.s11.intro" /></p>
          <ul>
            <li><T k="terms.s11.i1" /></li>
            <li><T k="terms.s11.i2" /></li>
            <li><T k="terms.s11.i3" /></li>
            <li><T k="terms.s11.i4" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s12.title')}>
          <p><T k="terms.s12.p1" /></p>
        </Section>

        <Section title={t('terms.s13.title')}>
          <p><T k="terms.s13.p1" /></p>
        </Section>

        {/* Complaints, locker failure and how the terms change: three things a
            consumer contract has to answer and this one did not. */}
        <Section title={t('terms.s15.title')}>
          <p><T k="terms.s15.intro" /></p>
          <ul>
            <li><T k="terms.s15.i1" /></li>
            <li><T k="terms.s15.i2" /></li>
            <li><T k="terms.s15.i3" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s16.title')}>
          <ul>
            <li><T k="terms.s16.i1" /></li>
            <li><T k="terms.s16.i2" /></li>
            <li><T k="terms.s16.i3" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s17.title')}>
          <p><T k="terms.s17.p1" /></p>
        </Section>

        {/* Suspension had no clause at all, while the code could do it — which
            means it was being done with no contractual basis and no notice. A
            term letting a trader shut a consumer out at will and without reasons
            is also squarely in the Directive 93/13 grey list (Annex 1(f)–(g)),
            so what is written here is the narrow version the code now enforces:
            stated grounds, an email, no suspension mid-booking, and no money
            moving while it lasts. */}
        <Section title={t('terms.s18.title')}>
          <ul>
            <li><T k="terms.s18.i1" /></li>
            <li><T k="terms.s18.i2" /></li>
            <li><T k="terms.s18.i3" /></li>
            <li><T k="terms.s18.i4" /></li>
          </ul>
        </Section>

        <Section title={t('terms.s14.title')}>
          <p><T k="terms.s14.p1" /></p>
          <TraderIdentity />
        </Section>
      </div>

    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 48 }}>
      <h2 style={{ fontFamily: theme.font.display, fontSize: 26, color: theme.ink, fontWeight: 500, margin: '0 0 16px', borderBottom: `1px solid ${theme.sand}`, paddingBottom: 12 }}>{title}</h2>
      <div style={{ color: theme.ink600, fontSize: 15, lineHeight: 1.8 }}>{children}</div>
    </div>
  );
}

